import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Badge, Modal, Form, Alert, Row, Col, ButtonGroup, Dropdown } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSync, faCheck, faSave, faExclamationTriangle, faTrash, faInfoCircle, faDownload, faCopy } from '@fortawesome/free-solid-svg-icons';
import c2Service from '../../services/c2Service';
import projectService from '../../services/projectService';
import { toast } from 'react-toastify';
import EvasionPanel from './EvasionPanel';
import BeaconContextMenu from './BeaconContextMenu';

const BeaconManager = ({ beacons, selectedBeacon, onSelectBeacon, onRefresh, wsConnected, projectId, project }) => {
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [beaconToApprove, setBeaconToApprove] = useState(null);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [beaconToConfig, setBeaconToConfig] = useState(null);
  const [configData, setConfigData] = useState({ sleep: 60, jitter: 30 });
  const [filter, setFilter] = useState('all'); // all, alive, pending
  const [error, setError] = useState('');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [availableProjects, setAvailableProjects] = useState([]);
  const [selectedProjectForApproval, setSelectedProjectForApproval] = useState('');
  const [contextMenu, setContextMenu] = useState(null);
  const [stagePayloads, setStagePayloads] = useState([]);
  const [loadingPayloads, setLoadingPayloads] = useState(false);

  useEffect(() => {
    loadPayloads();
  }, []);

  const loadPayloads = async () => {
    setLoadingPayloads(true);
    try {
      const res = await c2Service.getStageInfo();
      setStagePayloads(res.data?.payloads || []);
    } catch (err) {
      console.error('Failed to load payloads:', err);
    } finally {
      setLoadingPayloads(false);
    }
  };

  const handleDownload = async (p) => {
    try {
      await c2Service.downloadStagePayload(p.platform, p.arch, p.variant);
    } catch (err) {
      console.error('Download error:', err);
    }
  };

  const copyDownloadUrl = (p) => {
    const url = `${window.location.origin}/api/c2/stage/download?platform=${p.platform}&arch=${p.arch}&variant=${p.variant}`;
    navigator.clipboard.writeText(url);
  };

  // Close context menu on click elsewhere
  useEffect(() => {
    const handleClick = () => setContextMenu(null);
    if (contextMenu) {
      document.addEventListener('click', handleClick);
      return () => document.removeEventListener('click', handleClick);
    }
  }, [contextMenu]);

  const loadProjects = async () => {
    try {
      const response = await projectService.getAllProjects();
      const projects = response.data?.data || response.data || [];
      setAvailableProjects(projects.filter(p => p.status === 'active'));
    } catch (error) {
      console.error('Failed to load projects:', error);
    }
  };

  const handleBeaconContextMenu = (e, beacon) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({
      beacon,
      position: { x: e.clientX, y: e.clientY }
    });
  };

  const handleApprove = async () => {
    const targetProjectId = projectId || selectedProjectForApproval;

    if (!targetProjectId) {
      setError('Please select a project to assign this beacon to.');
      return;
    }

    try {
      await c2Service.approveBeacon(beaconToApprove._id, targetProjectId);
      setShowApproveModal(false);
      setBeaconToApprove(null);
      setSelectedProjectForApproval('');
      setError('');
      onRefresh();
    } catch (error) {
      console.error('Failed to approve beacon:', error);
      setError(error.response?.data?.error || 'Failed to approve beacon');
    }
  };

  const openApproveModal = (beacon) => {
    setBeaconToApprove(beacon);
    setShowApproveModal(true);
    setError('');
    if (!projectId) {
      loadProjects();
    }
  };

  const handleKill = async (beacon) => {
    if (!window.confirm(`Are you sure you want to kill beacon ${beacon.beaconId}?\n\nThis will send an exit command to the beacon but keep it in the database.`)) {
      return;
    }

    try {
      await c2Service.killBeacon(beacon._id);
      onRefresh();
    } catch (error) {
      console.error('Failed to kill beacon:', error);
      setError(error.response?.data?.error || 'Failed to kill beacon');
    }
  };

  const handleDelete = async (beacon) => {
    if (!window.confirm(`PERMANENTLY DELETE beacon ${beacon.beaconId}?\n\nThis will:\n• Delete the beacon from the database\n• Delete all associated tasks\n• Delete all session history\n• This action CANNOT be undone!\n\nAre you absolutely sure?`)) {
      return;
    }

    try {
      await c2Service.deleteBeacon(beacon._id);
      onRefresh();
    } catch (error) {
      console.error('Failed to delete beacon:', error);
      setError(error.response?.data?.error || 'Failed to delete beacon');
    }
  };

  const handleUpdateConfig = async () => {
    try {
      await c2Service.updateBeacon(beaconToConfig._id, {
        'config.sleep': parseInt(configData.sleep),
        'config.jitter': parseInt(configData.jitter)
      });
      toast.success(`Beacon config updated — sleep ${configData.sleep}s ± ${configData.jitter}% (task queued)`);
      setShowConfigModal(false);
      setBeaconToConfig(null);
      onRefresh();
    } catch (error) {
      console.error('Failed to update beacon config:', error);
      toast.error(error.response?.data?.error || 'Failed to update beacon config');
    }
  };

  const filteredBeacons = beacons.filter(b => {
    if (filter === 'alive') return b.isAlive;
    if (filter === 'pending') return !b.isApproved;
    return true;
  });

  const getTimeSinceLastSeen = (lastSeen) => {
    const seconds = Math.floor((new Date() - new Date(lastSeen)) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  return (
    <>
      <Card>
        {/* FORCED Horizontal Header */}
        <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem 1rem', backgroundColor: '#374151', borderBottom: '1px solid #4b5563', flexWrap: 'wrap', gap: '1rem' }}>
          <strong style={{ color: '#fff' }}>Beacons ({filteredBeacons?.length || 0})</strong>
          <div style={{ display: 'flex', flexDirection: 'row', gap: '0.5rem', alignItems: 'center' }}>
            <div style={{ display: 'flex', flexDirection: 'row', gap: '0' }}>
              <button
                onClick={() => setFilter('all')}
                style={{
                  padding: '0.375rem 0.75rem',
                  fontSize: '0.875rem',
                  backgroundColor: filter === 'all' ? '#3b82f6' : 'transparent',
                  color: filter === 'all' ? '#fff' : '#3b82f6',
                  border: '1px solid #3b82f6',
                  borderRight: 'none',
                  cursor: 'pointer',
                  borderTopLeftRadius: '0.375rem',
                  borderBottomLeftRadius: '0.375rem'
                }}
              >
                All
              </button>
              <button
                onClick={() => setFilter('alive')}
                style={{
                  padding: '0.375rem 0.75rem',
                  fontSize: '0.875rem',
                  backgroundColor: filter === 'alive' ? '#10b981' : 'transparent',
                  color: filter === 'alive' ? '#fff' : '#10b981',
                  border: '1px solid #10b981',
                  borderRight: 'none',
                  cursor: 'pointer'
                }}
              >
                Alive
              </button>
              <button
                onClick={() => setFilter('pending')}
                style={{
                  padding: '0.375rem 0.75rem',
                  fontSize: '0.875rem',
                  backgroundColor: filter === 'pending' ? '#f59e0b' : 'transparent',
                  color: filter === 'pending' ? '#fff' : '#f59e0b',
                  border: '1px solid #f59e0b',
                  cursor: 'pointer',
                  borderTopRightRadius: '0.375rem',
                  borderBottomRightRadius: '0.375rem'
                }}
              >
                Pending
              </button>
            </div>
            <button
              onClick={onRefresh}
              style={{
                padding: '0.375rem 0.75rem',
                fontSize: '0.875rem',
                backgroundColor: 'transparent',
                color: '#9ca3af',
                border: '1px solid #4b5563',
                borderRadius: '0.375rem',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.375rem'
              }}
            >
              <FontAwesomeIcon icon={faSync} />
              Refresh
            </button>
          </div>
        </div>
        <div style={{ padding: 0 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 0 }}>
            <thead>
              <tr style={{ backgroundColor: '#374151', borderBottom: '1px solid #4b5563' }}>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#9ca3af' }}>Status</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#9ca3af' }}>Beacon ID</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#9ca3af' }}>Hostname</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#9ca3af' }}>OS</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#9ca3af' }}>User</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#9ca3af' }}>Last Seen</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#9ca3af' }}>Sleep</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#9ca3af' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredBeacons.map(beacon => (
                <tr
                  key={beacon._id}
                  onClick={() => { onSelectBeacon(beacon); setShowDetailsModal(true); }}
                  onContextMenu={(e) => handleBeaconContextMenu(e, beacon)}
                  style={{
                    cursor: 'pointer',
                    backgroundColor: selectedBeacon?._id === beacon._id ? '#374151' : 'transparent',
                    borderBottom: '1px solid #374151'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1f2937'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = selectedBeacon?._id === beacon._id ? '#374151' : 'transparent'}
                >
                  <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', color: '#e5e7eb' }}>
                    {!beacon.isApproved && (
                      <Badge bg="warning" className="me-1">Pending</Badge>
                    )}
                    {beacon.isAlive ? (
                      <Badge bg="success">● Alive</Badge>
                    ) : (
                      <Badge bg="secondary">○ Offline</Badge>
                    )}
                  </td>
                  <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', color: '#e5e7eb' }}>
                    <code style={{ fontSize: '0.75rem', backgroundColor: '#1f2937', padding: '0.25rem 0.5rem', borderRadius: '0.25rem', color: '#10b981' }}>{beacon.beaconId}</code>
                  </td>
                  <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', color: '#e5e7eb' }}>
                    {beacon.systemInfo?.hostname || 'N/A'}
                    {beacon.systemInfo?.isAdmin && (
                      <Badge bg="danger" className="ms-1" style={{ fontSize: '0.7rem' }}>Admin</Badge>
                    )}
                  </td>
                  <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', color: '#e5e7eb' }}>{beacon.systemInfo?.os || 'N/A'}</td>
                  <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', color: '#e5e7eb' }}>{beacon.systemInfo?.username || 'N/A'}</td>
                  <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', color: '#9ca3af' }}>
                    {getTimeSinceLastSeen(beacon.lastSeen)}
                  </td>
                  <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', color: '#9ca3af' }}>
                    {beacon.config?.sleep || 60}s ± {beacon.config?.jitter || 30}%
                  </td>
                  <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', color: '#e5e7eb' }} onClick={(e) => e.stopPropagation()}>
                    <Dropdown as={ButtonGroup} size="sm">
                      <Dropdown.Toggle variant="outline-secondary" size="sm" style={{ fontSize: '0.875rem' }}>
                        Actions
                      </Dropdown.Toggle>
                      <Dropdown.Menu style={{
                        backgroundColor: '#1f2937',
                        border: '1px solid #374151',
                        minWidth: '160px',
                        padding: '0.5rem 0'
                      }}>
                        {!beacon.isApproved && (
                          <Dropdown.Item
                            onClick={() => openApproveModal(beacon)}
                            style={{
                              color: '#e5e7eb',
                              padding: '0.5rem 1rem',
                              fontSize: '0.875rem',
                              backgroundColor: 'transparent',
                              border: 'none'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#374151'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            <FontAwesomeIcon icon={faCheck} className="me-2" /> Approve
                          </Dropdown.Item>
                        )}
                        <Dropdown.Item
                          onClick={() => {
                            setBeaconToConfig(beacon);
                            setConfigData({
                              sleep: beacon.config?.sleep || 60,
                              jitter: beacon.config?.jitter || 30
                            });
                            setShowConfigModal(true);
                          }}
                          style={{
                            color: '#e5e7eb',
                            padding: '0.5rem 1rem',
                            fontSize: '0.875rem',
                            backgroundColor: 'transparent',
                            border: 'none'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#374151'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          Configure
                        </Dropdown.Item>
                        <Dropdown.Divider style={{ borderColor: '#374151', margin: '0.5rem 0' }} />
                        <Dropdown.Item
                          onClick={() => handleKill(beacon)}
                          style={{
                            color: '#f59e0b',
                            padding: '0.5rem 1rem',
                            fontSize: '0.875rem',
                            backgroundColor: 'transparent',
                            border: 'none'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#78350f'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          Kill Beacon
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => handleDelete(beacon)}
                          style={{
                            color: '#ef4444',
                            padding: '0.5rem 1rem',
                            fontSize: '0.875rem',
                            backgroundColor: 'transparent',
                            border: 'none'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#7f1d1d'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          <FontAwesomeIcon icon={faTrash} /> Delete from Database
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredBeacons.length === 0 && (
            <div style={{ textAlign: 'center', color: '#9ca3af', padding: '3rem 0' }}>
              <p style={{ marginBottom: '0.5rem', fontSize: '1rem' }}>No beacons found</p>
              <small style={{ fontSize: '0.875rem' }}>Beacons will appear here after first check-in</small>
            </div>
          )}
        </div>

        {/* Agent Downloads */}
        {stagePayloads.length > 0 && (
          <div style={{ borderTop: '1px solid #4b5563', padding: '0.75rem 1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <FontAwesomeIcon icon={faDownload} style={{ color: '#9ca3af', fontSize: '0.75rem' }} />
              <strong style={{ color: '#9ca3af', fontSize: '0.8rem' }}>Agent Downloads</strong>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {stagePayloads.map((p, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: '0.375rem',
                  backgroundColor: '#1f2937', border: '1px solid #374151',
                  borderRadius: '0.375rem', padding: '0.375rem 0.75rem', fontSize: '0.8rem'
                }}>
                  <span style={{ color: '#e5e7eb' }}>
                    {p.platform}/{p.arch} ({p.variant})
                  </span>
                  <button
                    onClick={() => handleDownload(p)}
                    style={{ color: '#10b981', background: 'none', border: 'none', cursor: 'pointer', padding: '0 0.25rem' }}
                    title="Download"
                  >
                    <FontAwesomeIcon icon={faDownload} />
                  </button>
                  <button
                    onClick={() => copyDownloadUrl(p)}
                    style={{ color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer', padding: '0 0.25rem' }}
                    title="Copy download URL"
                  >
                    <FontAwesomeIcon icon={faCopy} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* Beacon Details Modal */}
      <Modal show={showDetailsModal && selectedBeacon} onHide={() => setShowDetailsModal(false)} size="lg" centered>
        <Modal.Header closeButton style={{ backgroundColor: '#1f2937', borderBottom: '1px solid #374151' }}>
          <Modal.Title style={{ color: '#f9fafb' }}>
            Beacon Details: {selectedBeacon?.beaconId}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: '#111827', color: '#f9fafb' }}>
          {selectedBeacon && (
            <Row>
              <Col md={6}>
                <h6>System Information</h6>
                <Table size="sm" bordered>
                  <tbody>
                    <tr>
                      <td><strong>Hostname:</strong></td>
                      <td>{selectedBeacon.systemInfo?.hostname}</td>
                    </tr>
                    <tr>
                      <td><strong>OS:</strong></td>
                      <td>{selectedBeacon.systemInfo?.os}</td>
                    </tr>
                    <tr>
                      <td><strong>Architecture:</strong></td>
                      <td>{selectedBeacon.systemInfo?.architecture}</td>
                    </tr>
                    <tr>
                      <td><strong>Username:</strong></td>
                      <td>{selectedBeacon.systemInfo?.username}</td>
                    </tr>
                    <tr>
                      <td><strong>Domain:</strong></td>
                      <td>{selectedBeacon.systemInfo?.domain || 'N/A'}</td>
                    </tr>
                    <tr>
                      <td><strong>Admin:</strong></td>
                      <td>
                        {selectedBeacon.systemInfo?.isAdmin ? (
                          <Badge bg="danger">Yes</Badge>
                        ) : (
                          <Badge bg="secondary">No</Badge>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td><strong>IP Addresses:</strong></td>
                      <td>
                        {selectedBeacon.systemInfo?.ipAddresses?.join(', ') || 'N/A'}
                      </td>
                    </tr>
                    <tr>
                      <td><strong>External IP:</strong></td>
                      <td>{selectedBeacon.systemInfo?.externalIp || 'N/A'}</td>
                    </tr>
                  </tbody>
                </Table>
              </Col>
              <Col md={6}>
                <h6>Configuration</h6>
                <Table size="sm" bordered>
                  <tbody>
                    <tr>
                      <td><strong>Sleep:</strong></td>
                      <td>{selectedBeacon.config?.sleep || 60} seconds</td>
                    </tr>
                    <tr>
                      <td><strong>Jitter:</strong></td>
                      <td>{selectedBeacon.config?.jitter || 30}%</td>
                    </tr>
                    <tr>
                      <td><strong>Kill Date:</strong></td>
                      <td>
                        {selectedBeacon.config?.killDate
                          ? new Date(selectedBeacon.config.killDate).toLocaleString()
                          : 'None'}
                      </td>
                    </tr>
                    <tr>
                      <td><strong>Working Hours:</strong></td>
                      <td>
                        {selectedBeacon.config?.workingHours?.enabled
                          ? `${selectedBeacon.config.workingHours.startHour}:00 - ${selectedBeacon.config.workingHours.endHour}:00`
                          : 'Disabled'}
                      </td>
                    </tr>
                  </tbody>
                </Table>

                <h6 className="mt-3">Statistics</h6>
                <Table size="sm" bordered>
                  <tbody>
                    <tr>
                      <td><strong>Check-ins:</strong></td>
                      <td>{selectedBeacon.checkInCount || 0}</td>
                    </tr>
                    <tr>
                      <td><strong>Created:</strong></td>
                      <td>{new Date(selectedBeacon.createdAt).toLocaleString()}</td>
                    </tr>
                    <tr>
                      <td><strong>Last Seen:</strong></td>
                      <td>{new Date(selectedBeacon.lastSeen).toLocaleString()}</td>
                    </tr>
                  </tbody>
                </Table>
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer style={{ backgroundColor: '#1f2937', borderTop: '1px solid #374151' }}>
          <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Evasion Control Panel */}
      {selectedBeacon && selectedBeacon.isApproved && selectedBeacon.isAlive && (
        <Card className="mt-3">
          <EvasionPanel
            beaconId={selectedBeacon._id}
            onEvasionComplete={(status) => {
              // Update beacon status or show notification
              console.log('Evasion status:', status);
              onRefresh();
            }}
          />
        </Card>
      )}

      {/* Approve Beacon Modal */}
      <Modal
        show={showApproveModal}
        onHide={() => setShowApproveModal(false)}
        centered
        backdrop="static"
        keyboard={true}
      >
        <Modal.Header closeButton style={{ backgroundColor: '#1f2937', borderBottom: '1px solid #374151', color: '#fff' }}>
          <Modal.Title style={{ color: '#fff' }}>Approve Beacon</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: '#1f2937', color: '#e5e7eb' }}>
          <Alert variant="warning">
            <strong><FontAwesomeIcon icon={faExclamationTriangle} /> Security Check</strong>
            <p className="mb-0 mt-2">
              Review beacon details before approval. Approving a beacon allows it to receive and execute commands.
            </p>
          </Alert>
          {beaconToApprove && (
            <>
              <p><strong>Beacon ID:</strong> {beaconToApprove.beaconId}</p>
              <p><strong>Hostname:</strong> {beaconToApprove.systemInfo?.hostname}</p>
              <p><strong>User:</strong> {beaconToApprove.systemInfo?.username}</p>
              <p><strong>OS:</strong> {beaconToApprove.systemInfo?.os}</p>
              <p><strong>External IP:</strong> {beaconToApprove.systemInfo?.externalIp}</p>

              <hr />

              <div className="mb-3">
                <strong>Project Assignment:</strong>
                {projectId ? (
                  <div className="mt-2 p-3 rounded" style={{ backgroundColor: '#374151', border: '1px solid #4b5563' }}>
                    {project ? (
                      <>
                        <div><strong>{project.name}</strong></div>
                        <div className="small" style={{ color: '#9ca3af' }}>{project.client} • {project.status}</div>
                      </>
                    ) : (
                      <div><strong>Current Project</strong></div>
                    )}
                    <div className="small mt-2" style={{ color: '#9ca3af' }}>
                      <FontAwesomeIcon icon={faInfoCircle} /> Beacon will be assigned to this project. All tasks will be tracked as project actions.
                    </div>
                  </div>
                ) : (
                  <Form.Group className="mt-2">
                    <Form.Select
                      value={selectedProjectForApproval}
                      onChange={(e) => setSelectedProjectForApproval(e.target.value)}
                      style={{ backgroundColor: '#374151', color: '#e5e7eb', border: '1px solid #4b5563' }}
                    >
                      <option value="">Select a project...</option>
                      {availableProjects.map(proj => (
                        <option key={proj._id} value={proj._id}>
                          {proj.name} ({proj.client})
                        </option>
                      ))}
                    </Form.Select>
                    <div className="small mt-2" style={{ color: '#9ca3af' }}>
                      <FontAwesomeIcon icon={faInfoCircle} /> Beacon will be assigned to the selected project.
                    </div>
                  </Form.Group>
                )}
                {error && <div className="text-danger small mt-2">{error}</div>}
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer style={{ backgroundColor: '#1f2937', borderTop: '1px solid #374151' }}>
          <Button variant="secondary" onClick={() => { setShowApproveModal(false); setError(''); setSelectedProjectForApproval(''); }}>
            Cancel
          </Button>
          <Button variant="success" onClick={handleApprove} disabled={!projectId && !selectedProjectForApproval}>
            <FontAwesomeIcon icon={faCheck} /> Approve & Assign to Project
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Configure Beacon Modal */}
      <Modal
        show={showConfigModal}
        onHide={() => setShowConfigModal(false)}
        centered
        backdrop="static"
        keyboard={true}
      >
        <Modal.Header closeButton style={{ backgroundColor: '#1f2937', borderBottom: '1px solid #374151', color: '#fff' }}>
          <Modal.Title style={{ color: '#fff' }}>Configure Beacon</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: '#1f2937', color: '#e5e7eb' }}>
          {beaconToConfig && (
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Sleep Interval (seconds)</Form.Label>
                <Form.Control
                  type="number"
                  value={configData.sleep}
                  onChange={(e) => setConfigData({ ...configData, sleep: e.target.value })}
                  min="1"
                  max="3600"
                />
                <Form.Text className="text-muted">
                  Time between check-ins (1-3600 seconds)
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Jitter (%)</Form.Label>
                <Form.Control
                  type="number"
                  value={configData.jitter}
                  onChange={(e) => setConfigData({ ...configData, jitter: e.target.value })}
                  min="0"
                  max="100"
                />
                <Form.Text className="text-muted">
                  Randomization percentage (0-100%)
                </Form.Text>
              </Form.Group>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer style={{ backgroundColor: '#1f2937', borderTop: '1px solid #374151' }}>
          <Button variant="secondary" onClick={() => setShowConfigModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleUpdateConfig}>
            <FontAwesomeIcon icon={faSave} /> Save Configuration
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Context Menu */}
      {contextMenu && (
        <BeaconContextMenu
          beacon={contextMenu.beacon}
          position={contextMenu.position}
          onClose={() => setContextMenu(null)}
          onTaskCreated={() => {
            setContextMenu(null);
            if (onRefresh) onRefresh();
          }}
        />
      )}
    </>
  );
};

export default BeaconManager;
